import React, {
  FunctionComponent,
  useState,
  SetStateAction,
  Dispatch,
  useEffect,
  useContext,
  useCallback,
} from "react";
import URI from "urijs";
import classNames from "classnames";
import {
  useTranslate,
  makePlanUrlPartForApi,
  EnrollmentPages,
  getValidationObject,
  makePlanDisplayId,
  parseSearchParams,
  useQueryParams,
} from "../../helpers";
import CurrentFormPage from "./CurrentFormPage";
import { AppContext } from "../../app/store";
import { getPlan } from "../../api";
import {
  ActionType,
  Plan,
  IdParts,
  Sex,
  Gender,
  GetBenefitsFrom,
  EnrollData2022,
  EnrollData2023,
} from "../../@types";
import { MCTDateFromString } from "../../helpers/dateHelpers";
import { RouteComponentProps } from "react-router";
import { useFlags } from "launchdarkly-react-client-sdk";
import states from "../../helpers/states";
import { isCostPlanWithoutDrugs } from "../../helpers/enrollmentFormHelpers";
import { getYearPartInfo } from "../../helpers/yearPartHelpers";
import { AnalyticsActionType } from "../../app/contexts/Analytics/types";

export interface EnrollmentFormProps2022 {
  enrollData: EnrollData2022;
  setEnrollData: Dispatch<SetStateAction<EnrollData2022>>;
  nextPage: () => void;
  previousPage: () => void;
  plan: Plan;
  goToPage: (page: EnrollmentPages) => void;
  inReview: boolean;
}

export interface EnrollmentFormProps2023 {
  enrollData: EnrollData2023;
  setEnrollData: Dispatch<SetStateAction<EnrollData2023>>;
  nextPage: () => void;
  previousPage: () => void;
  plan: Plan;
  goToPage: (page: EnrollmentPages) => void;
  inReview: boolean;
}

export const emptyEnrollData2022: EnrollData2022 = {
  additionalDrugCoverage: {
    name: "",
    id: "",
    group: "",
  },
  address1: "",
  address2: "",
  authorizedRepresentative: {
    name: "",
    relationship: "",
    addressLine1: "",
    city: "",
    state: "",
    zip: "",
    phone: { areaCode: "", exchangeCode: "", lineNumber: "" },
  },
  city: "",
  county: undefined,
  dob: { day: "", month: "", year: "" },
  email: "",
  firstName: "",
  hasSepOtherReason: false,
  lastName: "",
  mailingAddress1: "",
  mailingAddress2: "",
  mailingCity: "",
  mailingState: "",
  mailingZip: "",
  medicareNumber: "",
  middleInitial: "",
  premiumWithhold: GetBenefitsFrom.WITHHOLD_NONE,
  primaryCareName: "",
  primaryPhone: { areaCode: "", exchangeCode: "", lineNumber: "" },
  sepOtherReason: "",
  sepReasons: [],
  sex: Sex.NONE,
  ssn: { areaNumber: "", groupNumber: "", serialNumber: "" },
  state: "",
  zip: "",
};

export const emptyEnrollData2023: EnrollData2023 = {
  additionalDrugCoverage: {
    name: "",
    id: "",
    group: "",
  },
  address1: "",
  address2: "",
  authorizedRepresentative: {
    name: "",
    relationship: "",
    addressLine1: "",
    city: "",
    state: "",
    zip: "",
    phone: { areaCode: "", exchangeCode: "", lineNumber: "" },
  },
  city: "",
  county: undefined,
  dob: { day: "", month: "", year: "" },
  ethnicities: [],
  email: "",
  firstName: "",
  hasSepOtherReason: false,
  lastName: "",
  mailingAddress1: "",
  mailingAddress2: "",
  mailingCity: "",
  mailingState: "",
  mailingZip: "",
  medicareNumber: "",
  middleInitial: "",
  premiumWithhold: GetBenefitsFrom.WITHHOLD_NONE,
  primaryCareName: "",
  primaryPhone: { areaCode: "", exchangeCode: "", lineNumber: "" },
  races: [],
  sepOtherReason: "",
  sepReasons: [],
  sex: Sex.NONE,
  ssn: { areaNumber: "", groupNumber: "", serialNumber: "" },
  state: "",
  zip: "",
};

export const EnrollOptionalLabel: FunctionComponent<{ label: string }> = ({
  label,
}) => {
  const t = useTranslate();

  return (
    <>
      <span>{label}</span>{" "}
      <span className="ds-u-text-transform--lowercase">
        {t("enroll_form.optional")}
      </span>
    </>
  );
};

const EnrollmentFormPage: FunctionComponent<RouteComponentProps> = ({
  history,
  location,
  match,
}) => {
  const t = useTranslate();
  const {
    state: { selectedPlan, beneficiary, zipcode, county, language },
    dispatch,
  } = useContext(AppContext);
  const [orderedPages, setOrderedPages] = useState<EnrollmentPages[]>([]);
  const [stepPages, setStepPages] = useState<EnrollmentPages[]>([]);
  const [includeSep, setIncludeSep] = useState(false);
  useQueryParams({ history, location, match });

  const flags = useFlags();

  const { mctCurrentPlanYear } = flags;

  const { isOutsideOpenEnrollment } = getYearPartInfo(flags);

  let defaultEnrollData: EnrollData2022 | EnrollData2023;

  if (selectedPlan?.contract_year === "2023") {
    defaultEnrollData = emptyEnrollData2023;
  } else {
    defaultEnrollData = emptyEnrollData2022;
  }

  if (beneficiary) {
    let sex = Sex.NONE;

    if (beneficiary.gender === Gender.FEMALE) {
      sex = Sex.FEMALE;
    } else if (beneficiary.gender === Gender.MALE) {
      sex = Sex.MALE;
    }

    let address1 = "";
    let address2 = "";
    let city = "";
    let state = "";

    if (beneficiary.mailing_address) {
      const {
        address_1,
        address_2,
        city: beneCity,
        state: beneState,
      } = beneficiary.mailing_address;
      address1 = address_1;
      address2 = address_2;
      city = beneCity;

      const stateObj = states.find(state => state.abbreviation == beneState);

      if (stateObj) {
        state = stateObj.fipsCode;
      }
    }

    defaultEnrollData = {
      ...defaultEnrollData,
      firstName:
        beneficiary.firstname && beneficiary.firstname.length
          ? beneficiary.firstname[0]
          : "",
      middleInitial:
        beneficiary.firstname.length > 1 && beneficiary.firstname[1].length
          ? beneficiary.firstname[1][0].trim()
          : "",
      lastName: beneficiary.lastname,
      dob: MCTDateFromString(beneficiary.birthdate),
      medicareNumber: beneficiary.mbi || "",
      ssn: {
        areaNumber: beneficiary.ssn?.substring(0, 3) || "",
        groupNumber: beneficiary.ssn?.substring(3, 5) || "",
        serialNumber: beneficiary.ssn?.substring(5, 9) || "",
      },
      sex,
      address1,
      address2,
      city,
      state,
      email: beneficiary.email || "",
    };
  }

  if (zipcode) {
    defaultEnrollData = {
      ...defaultEnrollData,
      zip: zipcode,
      county,
    };
  }

  const [enrollData, setEnrollData] = useState<EnrollData2022 | EnrollData2023>(
    defaultEnrollData
  );
  const [currentPage, setCurrentPage] = useState(EnrollmentPages.MedicareInfo);
  const [inReview, setInReview] = useState(false);
  const [needPlanCard, setNeedPlanCard] = useState(false);
  const [stepsTaken, setStepsTaken] = useState(1);

  const updateBeneEnrollData = () => {
    if (!beneficiary) return;
    defaultEnrollData = {
      ...defaultEnrollData,
      medicareNumber: beneficiary.mbi || "",
      ssn: {
        areaNumber: beneficiary.ssn?.substring(0, 3) || "",
        groupNumber: beneficiary.ssn?.substring(3, 5) || "",
        serialNumber: beneficiary.ssn?.substring(5, 9) || "",
      },
    };
    setEnrollData(defaultEnrollData);
  };

  useEffect(() => {
    updateBeneEnrollData();
    // @see /src/app/store/AppStore.tsx. These values are wiped from browser
    // storage and so need to be rehydrated with an API call
  }, [beneficiary?.mbi, beneficiary?.ssn]);

  const goToPage = useCallback(
    (page: EnrollmentPages, replace?: boolean): void => {
      if (page === EnrollmentPages.Review) {
        setInReview(true);
      }

      const uri = new URI(location.search);
      uri.setQuery("page", page || currentPage);
      location.search = uri.search();
      if (replace) {
        history.replace(location);
      } else {
        setStepsTaken(stepsTaken + 1);
        history.push(location);
      }
    },
    [location, language]
  );

  useEffect(() => {
    if (selectedPlan) {
      const stepPagesByYear = {
        "2022": [
          EnrollmentPages.MedicareInfo,
          EnrollmentPages.PersonalInfo,
          EnrollmentPages.Address,
          EnrollmentPages.GeneralQuestions,
          EnrollmentPages.Paying,
          EnrollmentPages.Agreement,
          EnrollmentPages.Review,
        ],
        "2023": [
          EnrollmentPages.MedicareInfo,
          EnrollmentPages.PersonalInfo,
          EnrollmentPages.Address,
          EnrollmentPages.GeneralQuestions,
          EnrollmentPages.Paying,
          EnrollmentPages.Agreement,
          EnrollmentPages.Review,
        ],
      };

      const orderedPagesByYear = {
        "2022": [...stepPagesByYear["2022"], EnrollmentPages.Enrolled],
        "2023": [...stepPagesByYear["2023"], EnrollmentPages.Enrolled],
      };

      let ordered = orderedPagesByYear[selectedPlan.contract_year];

      const selectedPlanIsCurrentPlanYear =
        mctCurrentPlanYear.toString() === selectedPlan.contract_year;

      const selectedPlanIs2021CostPlanWithoutDrugs =
        (selectedPlan.contract_year === "2022" ||
          selectedPlan.contract_year === "2023") &&
        isCostPlanWithoutDrugs(selectedPlan as Plan);

      const includeSepPage =
        (isOutsideOpenEnrollment || selectedPlanIsCurrentPlanYear) &&
        !selectedPlanIs2021CostPlanWithoutDrugs;

      setIncludeSep(includeSepPage);

      if (includeSepPage) {
        ordered = [EnrollmentPages.Sep, ...ordered];
      }

      setOrderedPages(ordered);
      setStepPages(stepPagesByYear[selectedPlan.contract_year]);
      setCurrentPage(ordered[0]);
    } else {
      history.push("/");
    }

    // selectedPlan can either be a Plan or SearchResultPlan.
    // - selectedPlan is a Plan object if enrolled from plan details page.
    // - selectedPlan is a SearchResultPlan object if enrolled from search result page.
    // We need the full Plan object for enrollment. If selectedPlan is a SearchResultPlan object, then we'd make an api call to get the full Plan object.
    // eslint-disable-next-line no-prototype-builtins
    if (selectedPlan && !selectedPlan.hasOwnProperty("address_1")) {
      setNeedPlanCard(true);
      const apiUrl = makePlanUrlPartForApi(selectedPlan as IdParts);

      getPlan(apiUrl).then((plan: Plan | undefined) => {
        dispatch({
          type: ActionType.UPDATE_SELECTED_PLAN,
          payload: plan,
        });
        setNeedPlanCard(false);
      });
    }
  }, []);

  useEffect(() => {
    const uri = new URI(location.search);
    const { page: pageInUrl } = parseSearchParams(uri.search(true));
    let page = orderedPages[0];

    if (orderedPages.includes(pageInUrl as EnrollmentPages)) {
      const pageIndex = orderedPages.indexOf(pageInUrl as EnrollmentPages);
      if (pageIndex > 0 && selectedPlan) {
        const validityOfPreviousPage = getValidationObject(
          enrollData,
          selectedPlan,
          orderedPages[pageIndex - 1]
        );
        if (validityOfPreviousPage.isValid) {
          page = orderedPages[pageIndex];
        } else {
          goToPage(page, true);
        }
      }
    } else {
      goToPage(page, true);
    }
    setCurrentPage(page);
  }, [location.search]);

  const includeSteps = stepPages.includes(currentPage);
  const currentPageIndex = orderedPages.indexOf(currentPage);

  const nextPage = useCallback((): void => {
    const currentPageIndex = orderedPages.indexOf(currentPage);
    if (selectedPlan) {
      dispatch({
        type: AnalyticsActionType.SEND_TEALIUM_EVENT,
        settings: {
          event_action: `Step ${currentPageIndex +
            1} Enrollment - ${currentPage}`,
          event_label: `Step ${currentPageIndex + 1} - ${
            currentPage === EnrollmentPages.Review ? "Submit" : "Next"
          } - ${selectedPlan.name} - ${selectedPlan.plan_type}`,
          other_props: {
            plan_name: selectedPlan.name,
            plan_id: makePlanDisplayId(selectedPlan),
            plan_type: selectedPlan.plan_type,
          },
        },
      });
    }

    if (currentPageIndex !== -1) {
      const nextPageIndex = currentPageIndex + 1;
      const nextPage = orderedPages[nextPageIndex];

      if (nextPage) {
        if (nextPage === EnrollmentPages.Review) {
          setInReview(true);
        }

        if (inReview && currentPage !== EnrollmentPages.Review) {
          goToPage(EnrollmentPages.Review);
        } else {
          goToPage(nextPage);
        }
      }
    }
  }, [orderedPages, selectedPlan, currentPage, inReview, language]);

  const previousPage = useCallback((): void => {
    const currentPageIndex = orderedPages.indexOf(currentPage);
    if (selectedPlan) {
      dispatch({
        type: AnalyticsActionType.SEND_TEALIUM_EVENT,
        settings: {
          event_action: `Step ${currentPageIndex +
            1} Enrollment - ${currentPage}`,
          event_label: `Step ${currentPageIndex + 1} - Back - ${
            selectedPlan.name
          } - ${selectedPlan.plan_type}`,
          other_props: {
            plan_name: selectedPlan.name,
            plan_id: makePlanDisplayId(selectedPlan),
            plan_type: selectedPlan.plan_type,
          },
        },
      });
    }

    if (currentPageIndex === 0) {
      history.go(stepsTaken * -1);
    } else if (currentPageIndex !== -1) {
      const previousPageIndex = currentPageIndex - 1;
      const previousPage = orderedPages[previousPageIndex];

      if (previousPage) {
        if (previousPage === EnrollmentPages.Review) {
          setInReview(true);
        }

        goToPage(previousPage);
      }
    }
  }, [orderedPages, selectedPlan, currentPage, language]);

  const stepIndex = includeSep ? currentPageIndex : currentPageIndex + 1;

  return (
    <div className="EnrollmentFormPage">
      <div className="EnrollmentFormPage__content">
        {includeSteps && (
          <div>
            <ul className="EnrollmentFormPage__steps">
              {stepPages.map((_page, pageIndex) => {
                return (
                  <li
                    key={pageIndex}
                    className={classNames("EnrollmentFormPage__step", {
                      "EnrollmentFormPage__step--active": pageIndex < stepIndex,
                    })}
                  ></li>
                );
              })}
            </ul>
            <p className="EnrollmentFormPage__stepsText">
              {t("enrollment_form.step")} {stepIndex} {t("of")}{" "}
              {stepPages.length}
            </p>
          </div>
        )}

        {selectedPlan && !needPlanCard && (
          <CurrentFormPage
            enrollData={enrollData}
            setEnrollData={setEnrollData}
            nextPage={nextPage}
            previousPage={previousPage}
            currentPage={currentPage}
            plan={selectedPlan as Plan}
            goToPage={goToPage}
            inReview={inReview}
          />
        )}
      </div>
    </div>
  );
};

export default EnrollmentFormPage;
